import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Route, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] p-12 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1534351590666-13e3e96b5017?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400)",
            backgroundSize: "cover",
          }}
        />
        <div className="relative">
          <img src={`${process.env.PUBLIC_URL || ""}/infra-insight-logo-wit.png`} alt="Infra Insight" className="h-10 w-auto" data-testid="login-logo" />
        </div>
        <div className="relative space-y-4 max-w-md">
          <h1 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Gedeelde planning voor het hele team.
          </h1>
          <p className="text-slate-300 text-base">
            PDOK-adressen, kleuren per medewerker en met één klik een slimme AI-route.
          </p>
        </div>
        <div className="relative text-xs font-mono uppercase tracking-widest text-slate-400">
          Nederlandse buitendienst · PDOK Locatieserver
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden">
            <img src={`${process.env.PUBLIC_URL || ""}/infra-insight-logo.png`} alt="Infra Insight" className="h-8 w-auto" data-testid="login-logo-mobile" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Inloggen</h2>
            <p className="text-sm text-slate-500 mt-1">Log in om je planning te bekijken.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres</Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@bedrijf.nl"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Wachtwoord</Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          {err && (
            <div data-testid="login-error" className="text-sm rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2">
              {err}
            </div>
          )}

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-[#E8490F] hover:bg-[#c73d0c] h-11 font-semibold transition-colors"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Inloggen</>}
          </Button>

          <div className="text-center">
            <Link
              data-testid="forgot-password-link"
              to="/wachtwoord-vergeten"
              className="text-sm text-slate-500 hover:text-[#E8490F] transition-colors"
            >
              Wachtwoord vergeten?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
